<template>
  <!-- XOL-602/603/604 (UX spec section 4): the column every signed-out page below the
       login form uses: the forgot form, the confirmation, the set/reset form, the invite
       form, the link states and the success state. It sits in Login.vue's .inner-box,
       under the client logo and above "Powered by", in place of the login form.

       This component is only the column and the shared typography. The classes below are
       applied by the PAGES to their own markup (h4/p/small/buttons), so they are reached
       with ::v-deep: a scoped rule would only match elements this template owns. -->
  <div class="login-panel">
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'login-panel'
}
</script>

<style lang="scss" scoped>

/* The 400px left-aligned column, as the login input's 400px cap. The mock adds the
   login form's horizontal 2.85rem (1.5rem on a phone) so inputs never touch the card edge;
   the UX spec gives the vertical padding only. */
.login-panel {
  max-width: 400px;
  width: calc(100% - 5.7rem);
  margin: 2.85rem auto;
  text-align: left;
}

@media (max-width: 479px) {
  .login-panel {
    width: calc(100% - 3rem);
    margin: 1.5rem auto;
  }
}

/* NEW STYLES (UX spec section 4). The global silver h4 on the translucent #FFFAFA96 card
   fails contrast, so the title is dark (the same justification as .remarks-heading); the
   copy is #606266 on it. */
.login-panel ::v-deep .login-panel__title {
  margin: 0 0 0.8rem;
  color: #303133;
}

.login-panel ::v-deep .login-panel__title:focus {
  outline: none;
}

.login-panel ::v-deep .login-panel__text {
  margin: 0 0 12px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
}

.login-panel ::v-deep .login-panel__meta {
  display: block;
  margin: 0 0 14px;
  color: #606266;
  font-size: 12px;
}

.login-panel ::v-deep .login-panel__loading {
  margin: 0;
  text-align: center;
  color: #606266;
  font-size: 14px;
}

.login-panel ::v-deep .login-panel__icon {
  margin: 0 0 0.5rem;
  text-align: center;
  font-size: 2rem;
}

.login-panel ::v-deep .el-alert {
  margin: 0 0 16px;
  text-align: left;
}

.login-panel ::v-deep .login-panel__actions {
  margin-top: 8px;
  text-align: center;
}

.login-panel ::v-deep .login-panel__actions > div {
  margin-bottom: 12px;
}

.login-panel ::v-deep .el-form-item {
  line-height: normal;
}

.login-panel ::v-deep .el-form-item__content {
  line-height: 40px;
}

/* PasswordForm's permanent hint is .text-muted (#909399), which is unreadable on the translucent
   login card, and on the reset and invite pages it is the only too-short feedback. Darken it here,
   scoped to the card (UX spec Problem 7: new hint lines use #606266). The Change Password dialog
   has a white background and keeps the global colour. The line-height resets the 40px that
   .el-form-item__content (above) gives a wrapped hint, which otherwise double-spaces its lines on
   a narrow card. */
.login-panel ::v-deep .password-form__hint {
  color: #606266;
  line-height: 1.4;
}

/* Icon-to-label gap on these pages' buttons. Element adds its 5px only after its OWN
   el-icon-* icons; these buttons use the FontAwesome `icon` prop and (per the mock) no leading
   space in the label. Scoped here, not global: the app-wide fix is design-system section 14 D5. */
.login-panel ::v-deep .el-button > i.fa + span {
  margin-left: 5px;
}

/* The Login ID the password manager should save the new password under (spec section 4). */
.login-panel ::v-deep .login-panel__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

</style>
